import React, { useState, useEffect } from 'react';
import Navigation from './Navigation';
import LightRays from './LightRays';

const InfoPage = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [timeSlots, setTimeSlots] = useState({ R1: [], R2: [], R3: [] });
  const [lastTimeSlot, setLastTimeSlot] = useState(null);

  // Update current time every second for display
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Generate time slots from 15:00 to 19:50 (10-minute intervals)
  // Start from 10 minutes before current time, but never before 15:00
  const generateTimeSlots = (currentTime) => {
    const slots = [];
    
    // Calculate start time: 10 minutes before current time, rounded to nearest 10 minutes
    let currentSlot = new Date(currentTime);
    currentSlot.setMinutes(currentSlot.getMinutes() - 10);
    const startMinute = currentSlot.getMinutes();
    const roundedMinute = Math.floor(startMinute / 10) * 10;
    currentSlot.setMinutes(roundedMinute);
    currentSlot.setSeconds(0);
    currentSlot.setMilliseconds(0);

    // Minimum time is 15:00
    const minTime = new Date(currentTime);
    minTime.setHours(15);
    minTime.setMinutes(0);
    minTime.setSeconds(0);
    minTime.setMilliseconds(0);

    // Maximum time is 19:50
    const maxTime = new Date(currentTime);
    maxTime.setHours(19);
    maxTime.setMinutes(50);
    maxTime.setSeconds(0);
    maxTime.setMilliseconds(0);

    // Start from the later of: calculated start time or 15:00
    if (currentSlot < minTime) {
      currentSlot = new Date(minTime);
    }

    // Generate up to 6 time slots from start time to 19:50
    while (currentSlot <= maxTime && slots.length < 6) {
      const timeString = currentSlot.toLocaleTimeString('cs-CZ', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
      slots.push({
        time: timeString,
        datetime: new Date(currentSlot)
      });
      currentSlot.setMinutes(currentSlot.getMinutes() + 10);
    }

    return slots;
  };

  // Generate time slots from 15:00 to 19:45 (15-minute intervals) for R2
  // Start from 15 minutes before current time, but never before 15:00
  const generateTimeSlots15Min = (currentTime) => {
    const slots = [];
    
    // Calculate start time: 15 minutes before current time, rounded to nearest 15 minutes
    let currentSlot = new Date(currentTime);
    currentSlot.setMinutes(currentSlot.getMinutes() - 15);
    const startMinute = currentSlot.getMinutes();
    const roundedMinute = Math.floor(startMinute / 15) * 15;
    currentSlot.setMinutes(roundedMinute);
    currentSlot.setSeconds(0);
    currentSlot.setMilliseconds(0);

    // Minimum time is 15:00
    const minTime = new Date(currentTime);
    minTime.setHours(15);
    minTime.setMinutes(0);
    minTime.setSeconds(0);
    minTime.setMilliseconds(0);

    // Maximum time is 19:45
    const maxTime = new Date(currentTime);
    maxTime.setHours(19);
    maxTime.setMinutes(45);
    maxTime.setSeconds(0);
    maxTime.setMilliseconds(0);

    // Start from the later of: calculated start time or 15:00
    if (currentSlot < minTime) {
      currentSlot = new Date(minTime);
    }

    // Generate up to 6 time slots from start time to 19:45
    while (currentSlot <= maxTime && slots.length < 6) {
      const timeString = currentSlot.toLocaleTimeString('cs-CZ', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
      slots.push({
        time: timeString,
        datetime: new Date(currentSlot)
      });
      
      // Move to next 15-minute slot
      currentSlot.setMinutes(currentSlot.getMinutes() + 15);
      if (currentSlot.getMinutes() === 60) {
        currentSlot.setMinutes(0);
        currentSlot.setHours(currentSlot.getHours() + 1);
      }
    }

    return slots;
  };

  // Fetch table data
  const fetchTableData = async () => {
    try {
      // Fetch data for all three tables and scanned data
      const [r1Response, r2Response, r3Response, scannedResponse] = await Promise.all([
        fetch('/backend/get_time_availability.php'),
        fetch('/backend/get_time_availability_mira.php'),
        fetch('/backend/get_time_availability_third.php'),
        fetch('/backend/get_scanned_data.php')
      ]);

      const r1Data = await r1Response.json();
      const r2Data = await r2Response.json();
      const r3Data = await r3Response.json();
      const scannedData = await scannedResponse.json();
      
      // Get scanned data for checking scanned status
      let scannedDataArray = [];
      if (scannedData.success) {
        scannedDataArray = scannedData.data;
        console.log('Scanned data loaded:', scannedData.data);
        console.log('Scanned data count:', scannedData.data.length);
      } else {
        console.error('Failed to load scanned data:', scannedData);
      }

      // Generate time slots - R1 and R3 use 10-minute intervals, R2 uses 15-minute intervals
      const r1SlotsRaw = generateTimeSlots(currentTime);
      const r2SlotsRaw = generateTimeSlots15Min(currentTime);
      const r3SlotsRaw = generateTimeSlots(currentTime);
      
      // Filter out unavailable times: R1 and R3 exclude :50, R2 excludes :45
      const r1SlotsFiltered = r1SlotsRaw.filter(slot => {
        const minutes = parseInt(slot.time.split(':')[1]);
        return minutes !== 50;
      });
      
      const r2SlotsFiltered = r2SlotsRaw.filter(slot => {
        const minutes = parseInt(slot.time.split(':')[1]);
        return minutes !== 45;
      });
      
      const r3SlotsFiltered = r3SlotsRaw.filter(slot => {
        const minutes = parseInt(slot.time.split(':')[1]);
        return minutes !== 50;
      });
      
      console.log('Generated R1 time slots:', r1SlotsFiltered.map(s => s.time));
      console.log('Generated R2 time slots:', r2SlotsFiltered.map(s => s.time));
      console.log('Generated R3 time slots:', r3SlotsFiltered.map(s => s.time));
      console.log('Scanned data:', scannedDataArray);
      console.log('Scanned data time slots:', scannedDataArray.map(s => s.time_slot));
      console.log('Scanned data sample:', scannedDataArray.length > 0 ? scannedDataArray[0] : 'No scanned data');
      
      // Map time slots with availability data
      const r1Slots = r1SlotsFiltered.map(slot => {
        const timeKey = slot.time;
        const isAvailable = r1Data.available_times && r1Data.available_times.includes(timeKey);
        
        // Check if this time slot has been scanned
        const isScanned = scannedDataArray.some(scan => {
          const matches = scan.time_slot === timeKey && scan.table == 1;
          if (matches) {
            console.log('Found scanned match for R1:', scan, 'timeKey:', timeKey);
          }
          return matches;
        });
        
        // Get team names and numbers for this time slot
        const teamsRaw = r1Data.team_names && r1Data.team_names[timeKey] ? r1Data.team_names[timeKey] : [];
        
        // Check scanned status for each team
        const teams = teamsRaw.map(team => {
          const teamScanned = scannedDataArray.some(scan => {
            // Normalize time slots (remove seconds if present)
            const normalizedScanTime = scan.time_slot?.substring(0, 5); // Get HH:MM format
            const normalizedTimeKey = timeKey.substring(0, 5); // Get HH:MM format
            
            const timeMatch = normalizedScanTime === normalizedTimeKey || scan.time_slot === timeKey;
            const tableMatch = scan.table == 1 || scan.table === '1' || scan.table === 1;
            const nameMatch = scan.team_name === team.team_name || 
                            scan.team_name?.toLowerCase() === team.team_name?.toLowerCase();
            
            if (timeMatch && tableMatch && nameMatch) {
              console.log('Found scanned match for R1:', {
                scan,
                team: team.team_name,
                timeKey,
                normalizedScanTime,
                normalizedTimeKey,
                timeMatch,
                tableMatch,
                nameMatch
              });
            }
            
            return timeMatch && tableMatch && nameMatch;
          });
          return {
            ...team,
            scanned: teamScanned
          };
        });
        
        // Get capacity info
        const capacityInfo = r1Data.capacity_info && r1Data.capacity_info[timeKey] 
          ? r1Data.capacity_info[timeKey] 
          : { current_capacity: 0, max_capacity: 0 };
        
        return {
          ...slot,
          available: isAvailable,
          scanned: isScanned,
          table: 'R1',
          teams: teams,
          capacityInfo: capacityInfo
        };
      });

      const r2Slots = r2SlotsFiltered.map(slot => {
        const timeKey = slot.time;
        const isAvailable = r2Data.available_times && r2Data.available_times.includes(timeKey);
        
        // Check if this time slot has been scanned
        const isScanned = scannedDataArray.some(scan => {
          const matches = scan.time_slot === timeKey && scan.table == 2;
          if (matches) {
            console.log('Found scanned match for R2:', scan, 'timeKey:', timeKey);
          }
          return matches;
        });
        
        // Get team names and numbers for this time slot
        const teamsRaw = r2Data.team_names && r2Data.team_names[timeKey] ? r2Data.team_names[timeKey] : [];
        
        // Check scanned status for each team
        const teams = teamsRaw.map(team => {
          const teamScanned = scannedDataArray.some(scan => {
            // Normalize time slots (remove seconds if present)
            const normalizedScanTime = scan.time_slot?.substring(0, 5); // Get HH:MM format
            const normalizedTimeKey = timeKey.substring(0, 5); // Get HH:MM format
            
            const timeMatch = normalizedScanTime === normalizedTimeKey || scan.time_slot === timeKey;
            const tableMatch = scan.table == 2 || scan.table === '2' || scan.table === 2;
            const nameMatch = scan.team_name === team.team_name || 
                            scan.team_name?.toLowerCase() === team.team_name?.toLowerCase();
            
            if (timeMatch && tableMatch && nameMatch) {
              console.log('Found scanned match for R2:', {
                scan,
                team: team.team_name,
                timeKey,
                normalizedScanTime,
                normalizedTimeKey,
                timeMatch,
                tableMatch,
                nameMatch
              });
            }
            
            return timeMatch && tableMatch && nameMatch;
          });
          return {
            ...team,
            scanned: teamScanned
          };
        });
        
        // Get capacity info
        const capacityInfo = r2Data.capacity_info && r2Data.capacity_info[timeKey] 
          ? r2Data.capacity_info[timeKey] 
          : { current_capacity: 0, max_capacity: 0 };
        
        return {
          ...slot,
          available: isAvailable,
          scanned: isScanned,
          table: 'R2',
          teams: teams,
          capacityInfo: capacityInfo
        };
      });

      const r3Slots = r3SlotsFiltered.map(slot => {
        const timeKey = slot.time;
        const isAvailable = r3Data.available_times && r3Data.available_times.includes(timeKey);
        
        // Check if this time slot has been scanned
        const isScanned = scannedDataArray.some(scan => {
          const matches = scan.time_slot === timeKey && scan.table == 3;
          if (matches) {
            console.log('Found scanned match for R3:', scan, 'timeKey:', timeKey);
          }
          return matches;
        });
        
        // Get team names and numbers for this time slot
        const teamsRaw = r3Data.team_names && r3Data.team_names[timeKey] ? r3Data.team_names[timeKey] : [];
        
        // Check scanned status for each team
        const teams = teamsRaw.map(team => {
          const teamScanned = scannedDataArray.some(scan => {
            // Normalize time slots (remove seconds if present)
            const normalizedScanTime = scan.time_slot?.substring(0, 5); // Get HH:MM format
            const normalizedTimeKey = timeKey.substring(0, 5); // Get HH:MM format
            
            const timeMatch = normalizedScanTime === normalizedTimeKey || scan.time_slot === timeKey;
            const tableMatch = scan.table == 3 || scan.table === '3' || scan.table === 3;
            const nameMatch = scan.team_name === team.team_name || 
                            scan.team_name?.toLowerCase() === team.team_name?.toLowerCase();
            
            if (timeMatch && tableMatch && nameMatch) {
              console.log('Found scanned match for R3:', {
                scan,
                team: team.team_name,
                timeKey,
                normalizedScanTime,
                normalizedTimeKey,
                timeMatch,
                tableMatch,
                nameMatch
              });
            }
            
            return timeMatch && tableMatch && nameMatch;
          });
          return {
            ...team,
            scanned: teamScanned
          };
        });
        
        // Get capacity info
        const capacityInfo = r3Data.capacity_info && r3Data.capacity_info[timeKey] 
          ? r3Data.capacity_info[timeKey] 
          : { current_capacity: 0, max_capacity: 0 };
        
        return {
          ...slot,
          available: isAvailable,
          scanned: isScanned,
          table: 'R3',
          teams: teams,
          capacityInfo: capacityInfo
        };
      });

      setTimeSlots({ R1: r1Slots, R2: r2Slots, R3: r3Slots });
    } catch (error) {
      console.error('Error fetching table data:', error);
      // Generate slots without availability data
      const r1SlotsRaw = generateTimeSlots(currentTime);
      const r2SlotsRaw = generateTimeSlots15Min(currentTime);
      const r3SlotsRaw = generateTimeSlots(currentTime);
      
      // Filter out unavailable times: R1 and R3 exclude :50, R2 excludes :45
      const r1SlotsFiltered = r1SlotsRaw.filter(slot => {
        const minutes = parseInt(slot.time.split(':')[1]);
        return minutes !== 50;
      });
      
      const r2SlotsFiltered = r2SlotsRaw.filter(slot => {
        const minutes = parseInt(slot.time.split(':')[1]);
        return minutes !== 45;
      });
      
      const r3SlotsFiltered = r3SlotsRaw.filter(slot => {
        const minutes = parseInt(slot.time.split(':')[1]);
        return minutes !== 50;
      });
      
      const r1Slots = r1SlotsFiltered.map(slot => ({ ...slot, available: true, table: 'R1', teams: [], capacityInfo: { current_capacity: 0, max_capacity: 0 } }));
      const r2Slots = r2SlotsFiltered.map(slot => ({ ...slot, available: true, table: 'R2', teams: [], capacityInfo: { current_capacity: 0, max_capacity: 0 } }));
      const r3Slots = r3SlotsFiltered.map(slot => ({ ...slot, available: true, table: 'R3', teams: [], capacityInfo: { current_capacity: 0, max_capacity: 0 } }));
      setTimeSlots({ R1: r1Slots, R2: r2Slots, R3: r3Slots });
    }
  };

  // Calculate current time slot (rounded to nearest 10 minutes for R1/R3, 15 for R2)
  const getCurrentTimeSlot = (time) => {
    const minutes = time.getMinutes();
    const hours = time.getHours();
    // Round to nearest 10 minutes for R1/R3
    const roundedMin10 = Math.floor(minutes / 10) * 10;
    return `${hours.toString().padStart(2, '0')}:${roundedMin10.toString().padStart(2, '0')}`;
  };

  // Fetch data when component mounts
  useEffect(() => {
    fetchTableData();
  }, []);

  // Fetch data when time slot actually changes (every 10 minutes)
  useEffect(() => {
    const currentSlot = getCurrentTimeSlot(currentTime);
    // Only refresh if the time slot has actually changed
    if (lastTimeSlot !== null && currentSlot !== lastTimeSlot) {
      fetchTableData();
    }
    setLastTimeSlot(currentSlot);
  }, [currentTime]); // Only depend on currentTime

  // Auto-refresh data every 10 seconds to show real-time updates (less frequent to reduce flickering)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTableData();
    }, 10000); // Refresh every 10 seconds instead of 5

    return () => clearInterval(interval);
  }, []);

  const formatCurrentTime = (date) => {
    return date.toLocaleTimeString('cs-CZ', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: false 
    });
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Light Rays Background */}
      <div className="fixed inset-0 w-full h-full" style={{ zIndex: 1 }}>
        <LightRays
          raysOrigin="top-center"
          raysColor="#ffffff"
          raysSpeed={1.5}
          lightSpread={0.8}
          rayLength={2.5}
          fadeDistance={2.0}
          followMouse={true}
          mouseInfluence={0.1}
          noiseAmount={0.1}
          distortion={0.05}
          className="custom-rays"
        />
      </div>

      {/* Navigation */}
      <div style={{ position: 'relative', zIndex: 10 }}>
        <Navigation />
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 py-8">
        <div className="max-w-full mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg">
              📊 Informace o rezervacích
            </h1>
            <div className="text-2xl text-white mb-6">
              Aktuální čas: <span className="text-blue-400 font-mono">{formatCurrentTime(currentTime)}</span>
            </div>
          </div>

          {/* Table */}
          <div className="bg-gray-900/80 border-2 border-white rounded-3xl p-10">
            <div className="overflow-x-auto">
              <table className="w-full text-white table-fixed">
                <colgroup>
                  <col className="w-1/3" />
                  <col className="w-1/3" />
                  <col className="w-1/3" />
                </colgroup>
                <thead>
                  <tr className="border-b-2 border-white">
                    <th className="text-left py-6 px-10 text-2xl font-bold border-r-2 border-white">🦇 Stezka záhrobím</th>
                    <th className="text-left py-6 px-10 text-2xl font-bold border-r-2 border-white">👻 Dům šepotů</th>
                    <th className="text-left py-6 px-10 text-2xl font-bold">🏚️ Prokletá komnata</th>
                  </tr>
                </thead>
                <tbody>
                  {timeSlots.R1.map((slot, index) => (
                    <tr key={index} className="border-b border-gray-600">
                      <td className="py-5 px-10 border-r-2 border-white">
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center justify-between">
                            <span className="text-2xl font-mono">{slot.time}</span>
                            <div className="flex items-center gap-2">
                              <span className={`px-4 py-2 rounded-full text-base font-semibold ${
                                slot.capacityInfo?.current_capacity > 0 
                                  ? 'bg-green-600 text-white' 
                                  : 'bg-red-600 text-white'
                              }`}>
                                {slot.capacityInfo ? `${slot.capacityInfo.current_capacity}/${slot.capacityInfo.max_capacity}` : '0/0'}
                              </span>
                              {slot.capacityInfo && (() => {
                                const reservedCount = slot.capacityInfo.max_capacity - slot.capacityInfo.current_capacity;
                                const scannedCount = slot.teams ? slot.teams.filter(t => t.scanned).length : 0;
                                // When full (0/8) and no scans, show reserved count as scanned count
                                const displayScanned = reservedCount > 0 && scannedCount === 0 && slot.capacityInfo.current_capacity === 0 
                                  ? reservedCount 
                                  : scannedCount;
                                return reservedCount > 0 ? (
                                  <span className="px-4 py-2 rounded-full text-base font-semibold bg-blue-600/30 text-white">
                                    {displayScanned}/{reservedCount}
                                  </span>
                                ) : null;
                              })()}
                            </div>
                          </div>
                          {slot.teams && slot.teams.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-1">
                              {slot.teams.map((team, i) => (
                                <span 
                                  key={i}
                                  className={`px-3 py-1 rounded-lg text-base break-words ${
                                    team.scanned 
                                      ? 'bg-blue-600/30 text-white' 
                                      : 'bg-yellow-600/30 text-white'
                                  }`}
                                >
                                  {team.team_name} ({team.team_number})
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-5 px-10 border-r-2 border-white">
                        {timeSlots.R2[index] ? (
                          <div className="flex flex-col gap-3">
                            <div className="flex items-center justify-between">
                              <span className="text-2xl font-mono">{timeSlots.R2[index].time}</span>
                              <div className="flex items-center gap-2">
                                <span className={`px-4 py-2 rounded-full text-base font-semibold ${
                                  timeSlots.R2[index].capacityInfo?.current_capacity > 0 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-red-600 text-white'
                                }`}>
                                  {timeSlots.R2[index].capacityInfo ? `${timeSlots.R2[index].capacityInfo.current_capacity}/${timeSlots.R2[index].capacityInfo.max_capacity}` : '0/0'}
                                </span>
                                {timeSlots.R2[index].capacityInfo && (() => {
                                  const reservedCount = timeSlots.R2[index].capacityInfo.max_capacity - timeSlots.R2[index].capacityInfo.current_capacity;
                                  const scannedCount = timeSlots.R2[index].teams ? timeSlots.R2[index].teams.filter(t => t.scanned).length : 0;
                                  // When full (0/8) and no scans, show reserved count as scanned count
                                  const displayScanned = reservedCount > 0 && scannedCount === 0 && timeSlots.R2[index].capacityInfo.current_capacity === 0 
                                    ? reservedCount 
                                    : scannedCount;
                                  return reservedCount > 0 ? (
                                    <span className="px-4 py-2 rounded-full text-base font-semibold bg-blue-600/30 text-white">
                                      {displayScanned}/{reservedCount}
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                            </div>
                            {timeSlots.R2[index].teams && timeSlots.R2[index].teams.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-1">
                                {timeSlots.R2[index].teams.map((team, i) => (
                                  <span 
                                    key={i}
                                    className={`px-3 py-1 rounded-lg text-base break-words ${
                                      team.scanned 
                                        ? 'bg-blue-600/30 text-white' 
                                        : 'bg-yellow-600/30 text-white'
                                    }`}
                                  >
                                    {team.team_name} ({team.team_number})
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-gray-500 text-base">-</div>
                        )}
                      </td>
                      <td className="py-5 px-10">
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center justify-between">
                            <span className="text-2xl font-mono">{timeSlots.R3[index]?.time || ''}</span>
                            <div className="flex items-center gap-2">
                              <span className={`px-4 py-2 rounded-full text-base font-semibold ${
                                timeSlots.R3[index]?.capacityInfo?.current_capacity > 0 
                                  ? 'bg-green-600 text-white' 
                                  : 'bg-red-600 text-white'
                              }`}>
                                {timeSlots.R3[index]?.capacityInfo ? `${timeSlots.R3[index].capacityInfo.current_capacity}/${timeSlots.R3[index].capacityInfo.max_capacity}` : '0/0'}
                              </span>
                              {timeSlots.R3[index]?.capacityInfo && (() => {
                                const reservedCount = timeSlots.R3[index].capacityInfo.max_capacity - timeSlots.R3[index].capacityInfo.current_capacity;
                                const scannedCount = timeSlots.R3[index].teams ? timeSlots.R3[index].teams.filter(t => t.scanned).length : 0;
                                // When full (0/8) and no scans, show reserved count as scanned count
                                const displayScanned = reservedCount > 0 && scannedCount === 0 && timeSlots.R3[index].capacityInfo.current_capacity === 0 
                                  ? reservedCount 
                                  : scannedCount;
                                return reservedCount > 0 ? (
                                  <span className="px-4 py-2 rounded-full text-base font-semibold bg-blue-600/30 text-white">
                                    {displayScanned}/{reservedCount}
                                  </span>
                                ) : null;
                              })()}
                            </div>
                          </div>
                          {timeSlots.R3[index]?.teams && timeSlots.R3[index].teams.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-1">
                              {timeSlots.R3[index].teams.map((team, i) => (
                                <span 
                                  key={i}
                                  className={`px-3 py-1 rounded-lg text-base break-words ${
                                    team.scanned 
                                      ? 'bg-blue-600/30 text-white' 
                                      : 'bg-yellow-600/30 text-white'
                                  }`}
                                >
                                  {team.team_name} ({team.team_number})
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-8 flex justify-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className="w-5 h-5 bg-green-600 rounded-full"></div>
              <span className="text-white text-lg">Volná místa</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-5 h-5 bg-red-600 rounded-full"></div>
              <span className="text-white text-lg">Obsazeno</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-5 h-5 bg-blue-600 rounded-full"></div>
              <span className="text-white text-lg">Naskenováno</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoPage;

